import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogTitle,
  Radio,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import * as Styled from "./styles";

export const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface CancelOrderModalProps {
  labelType: "CANCEL" | "DENY";
  isOpen: boolean;
  onClose(): unknown;
  onConfirm(body: unknown): unknown;
}

function CancelOrderModal({
  labelType,
  isOpen,
  onClose,
  onConfirm,
}: CancelOrderModalProps) {
  const { t } = useTranslation();
  const cancellationReasons = {
    logisticProblems: t("logisticProblems"),
    driversOff: t("driversOff"),
    gasDoPovoNotEligible: t("gasDoPovoNotEligible"),
    addressOutOfRange: t("addressOutOfRange"),
    addressIsRiskArea: t("addressIsRiskArea"),
    other: t("other"),
  };
  const [reason, setReason] = React.useState<string>("");
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<{ reasonDescription: string }>();
  const isCancelling = labelType === "CANCEL";

  function isChecked(key: string) {
    return reason === key;
  }

  function handleChangeReason(newReason: string) {
    setReason(newReason);
    setValue("reasonDescription", "");
  }

  function handleConfirm({ reasonDescription }: { reasonDescription: string }) {
    if (reason === "other") {
      onConfirm({ reason: reasonDescription, origin: "DealerPortal" });
    } else {
      onConfirm({
        reason: (cancellationReasons as any)[reason],
        origin: "DealerPortal",
      });
    }
  }

  return (
    <Dialog
      open={isOpen}
      TransitionComponent={Transition}
      onClose={onClose}
      style={{ zIndex: 1500 }}
    >
      <DialogTitle>
        {isCancelling ? t("cancelOrderTitle") : t("denyOrderTitle")}
      </DialogTitle>

      <Styled.ContentContainer>
        <Styled.ContentParagraphContainer>
          <DialogContentText>{t("cancelOrderContentText1")}</DialogContentText>
          <DialogContentText>{t("cancelOrderContentText2")}</DialogContentText>
        </Styled.ContentParagraphContainer>
        <div>
          <Styled.RadioContainer>
            <Radio
              checked={isChecked("logisticProblems")}
              onChange={() => handleChangeReason("logisticProblems")}
              id="logisticProblems"
              name="radio-buttons"
              inputProps={{
                "aria-label": cancellationReasons.logisticProblems,
              }}
            />
            <label htmlFor="driversOff">
              {cancellationReasons.logisticProblems}
            </label>
          </Styled.RadioContainer>
          <Styled.RadioContainer>
            <Radio
              checked={isChecked("driversOff")}
              onChange={() => handleChangeReason("driversOff")}
              id="driversOff"
              name="radio-buttons"
              inputProps={{ "aria-label": cancellationReasons.driversOff }}
            />
            <label htmlFor="driversOff">{cancellationReasons.driversOff}</label>
          </Styled.RadioContainer>
          <Styled.RadioContainer>
            <Radio
              checked={isChecked("addressOutOfRange")}
              onChange={() => handleChangeReason("addressOutOfRange")}
              id="addressOutOfRange"
              name="radio-buttons"
              inputProps={{
                "aria-label": cancellationReasons.addressOutOfRange,
              }}
            />
            <label htmlFor="addressOutOfRange">
              {cancellationReasons.addressOutOfRange}
            </label>
          </Styled.RadioContainer>
          <Styled.RadioContainer>
            <Radio
              checked={isChecked("addressIsRiskArea")}
              onChange={() => handleChangeReason("addressIsRiskArea")}
              id="addressIsRiskArea"
              name="radio-buttons"
              inputProps={{
                "aria-label": cancellationReasons.addressIsRiskArea,
              }}
            />
            <label htmlFor="addressIsRiskArea">
              {cancellationReasons.addressIsRiskArea}
            </label>
          </Styled.RadioContainer>
          <Styled.RadioContainer>
            <Radio
              checked={isChecked("gasDoPovoNotEligible")}
              onChange={() => handleChangeReason("gasDoPovoNotEligible")}
              id="gasDoPovoNotEligible"
              name="radio-buttons"
              inputProps={{
                "aria-label": cancellationReasons.gasDoPovoNotEligible,
              }}
            />
            <label htmlFor="gasDoPovoNotEligible">
              {cancellationReasons.gasDoPovoNotEligible}
            </label>
          </Styled.RadioContainer>
          <Styled.RadioContainer>
            <Radio
              checked={isChecked("other")}
              onChange={() => handleChangeReason("other")}
              id="other"
              name="radio-buttons"
              inputProps={{ "aria-label": cancellationReasons.other }}
            />
            <label htmlFor="other">{cancellationReasons.other}</label>
          </Styled.RadioContainer>
        </div>
        <Styled.InputTextContainer>
          <Styled.InputText
            placeholder={t("writeHere")}
            name="reasonDescription"
            register={register}
            required={!isChecked("other")}
            error={errors.reasonDescription}
            disabled={!isChecked("other")}
            registerOptions={{
              required: isChecked("other") ? t("requiredField") : undefined,
            }}
          />
        </Styled.InputTextContainer>
      </Styled.ContentContainer>
      <DialogActions>
        <Button onClick={onClose}>{t("back")}</Button>
        <Button
          color="secondary"
          onClick={handleSubmit(handleConfirm)}
          disabled={!reason?.length}
        >
          {isCancelling ? t("cancelOrder") : t("denyOrder")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CancelOrderModal;
